import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

export interface DetailRead<T> {
  status: 'LOADING' | 'READY' | 'ERROR';
  data: T | null;
  error: string | null;
}
export function detailFailure(error: unknown): string {
  if (error !== null && typeof error === 'object') {
    const e = error as { status?: unknown; code?: unknown; kind?: unknown };
    if (e.status === 404) return `对象不存在（404 · ${String(e.code ?? 'NOT_FOUND')}）`;
    if (typeof e.code === 'string') return `${e.code}（HTTP ${String(e.status ?? '未知')}）`;
    if (typeof e.kind === 'string') return `请求不可用（${e.kind}）`;
  }
  return '连接失败或响应无效（TRANSPORT / WIRE）';
}
/** One in-flight read, abort on identity change, and never render stale data after failure. */
export function useDetailRead<T>(key: string, read: (signal: AbortSignal) => Promise<T>) {
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{ key: string; state: DetailRead<T> }>({
    key,
    state: { status: 'LOADING', data: null, error: null },
  });
  useEffect(() => {
    let alive = true;
    let busy = false;
    let controller: AbortController | null = null;
    setResult({ key, state: { status: 'LOADING', data: null, error: null } });
    async function poll() {
      if (busy || !alive) return;
      busy = true;
      controller = new AbortController();
      try {
        const data = await read(controller.signal);
        if (alive) setResult({ key, state: { status: 'READY', data, error: null } });
      } catch (error) {
        if (alive)
          setResult({ key, state: { status: 'ERROR', data: null, error: detailFailure(error) } });
      } finally {
        busy = false;
      }
    }
    void poll();
    const timer = setInterval(() => {
      void poll();
    }, 1000);
    return () => {
      alive = false;
      clearInterval(timer);
      controller?.abort();
    };
  }, [key, read, attempt]);
  return {
    state:
      result.key === key ? result.state : { status: 'LOADING' as const, data: null, error: null },
    retry: () => setAttempt((n) => n + 1),
  };
}
export function DetailReadNotice<T>({
  state,
  retry,
  label,
}: {
  readonly state: DetailRead<T>;
  readonly retry: () => void;
  readonly label: string;
}) {
  if (state.status === 'READY') return null;
  return (
    <div className="nm-vs04-read" role="status">
      <p>{state.status === 'LOADING' ? `正在读取${label}…` : `${label}暂不可用：${state.error}`}</p>
      {state.status === 'ERROR' && (
        <button type="button" onClick={retry}>
          重试读取{label}
        </button>
      )}
    </div>
  );
}
export function Fields({ items }: { readonly items: readonly (readonly [string, ReactNode])[] }) {
  return (
    <dl className="nm-vs04-fields">
      {items.map(([label, text]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{text}</dd>
        </div>
      ))}
    </dl>
  );
}
export const refs = (items: readonly string[]) => items.join('；') || '无记录';
export const metricLabel = (metric: string) =>
  ({ TEMPERATURE: '温度', PARTICULATE: '颗粒物', THERMAL_SCORE: '热异常评分' })[metric] ?? metric;
export const displayValue = (n: number | null) => (n === null ? '不可用' : String(n));
export const reasonLabel = (reason: string) =>
  `${
    (
      {
        AWAITING_DEADLINE: '等待实际值及结算截止',
        MATCHED_ACTUAL: '实际观测已匹配',
        NO_MATCH: '目标窗口无匹配观测',
        EPOCH_CHANGED: '来源启用周期改变',
        NO_AVAILABILITY_EVIDENCE: '缺少处理可用性依据',
        DEADLINE_DATA_MISSING: '截止时实际数据缺失',
        UNTIMELY_PUBLICATION: '未及时发布',
        TARGET_EXPIRED: '目标到期，提示不再适用',
        CURRENT_OBSERVED_WARNING: '当前观测预警下的未来趋势',
        SOURCE_DISABLED: '来源停用',
      } as Record<string, string>
    )[reason] ?? '后端原因'
  } · ${reason}`;
